import axios, { client2 } from "@/config/axios-wrapper";
import { Job } from "@/types/job";
import { Meeting } from "@/types/meeting";
import { FavouritedJobseeker, Organization, Teammate } from "@/types/organization";

export const getOrganization = async (organizationId: string) => {
  const response = await axios.get<Organization>(`/organizations/${organizationId}`);
  return response.data;
};

export const updateOrganization = async (organizationId: string, data) => {
  const response = await axios.put(`organizations/${organizationId}`, data);
  return response.data;
};

export const updateOrganizationLogo = async (organizationId: string, data: FormData) => {
  const response = await axios.post<{ logo: string }>(`organizations/${organizationId}/logo`, data);
  return response.data;
};

export const updateOrganizationVideo = async (organizationId: string, data: FormData) => {
  const response = await axios.post<{ video: string }>(
    `organizations/${organizationId}/video`,
    data,
  );
  return response.data;
};

export const deleteOrganizationVideo = async (organizationId: string) => {
  const response = await axios.delete(`organizations/${organizationId}/video`);
  return response.data;
};

export const getOrganizationNotifications = async (
  data: { organizationId: string },
  options: { cursor?: string },
) => {
  const response = await axios.get(
    `${`/organizations/${data.organizationId}`}/notifications${
      options.cursor ? `?cursor=${options.cursor}` : ""
    }`,
  );

  return response.data;
};

export const createOrganizationFavourite = async ({
  organizationId,
  userId,
}: {
  organizationId: string;
  userId: string;
}) => {
  const response = await axios.post(`/organizations/${organizationId}/favourites/${userId}`);
  return response.data;
};

export const getOrganizationFavourites = async (
  organizationId: string,
  options?: { q?: string; sort?: string },
) => {
  const response = await axios.get<{ favourites: FavouritedJobseeker[]; total: number }>(
    `/organizations/${organizationId}/favourites`,
    {
      params: {
        q: options?.q,
        // skip:0,
        // limit:20,
        sort: options?.sort,
      },
    },
  );
  return response.data;
};

export const deleteOrganizationFavourite = async ({
  organizationId,
  userId,
}: {
  organizationId: string;
  userId: string;
}) => {
  const response = await axios.delete(`/organizations/${organizationId}/favourites/${userId}`);
  return response.data;
};

export const getSavedRoles = async (organizationId: string) => {
  const response = await axios.get(`/organizations/${organizationId}/saved-roles`);
  return response.data;
};

export const createOrganizationMeeting = async (
  organizationId: string,
  data: {
    application_id: string;
    timezone: string;
    start_time: string;
    duration: number;
    mode: string;
    address?: {
      line1: string;
      line2: string;
      country: string;
      state: string;
      city: string;
      zip_code: string;
      details: string;
    };
    details: string;
  },
) => {
  const response = await axios.post<Meeting>(`/organizations/${organizationId}/meetings`, data);
  return response.data;
};

export const uploadOrganizationMeetingFile = async (
  {
    organizationId,
    meetingId,
  }: {
    organizationId: string;
    meetingId: string;
  },
  formData: FormData,
) => {
  const response = await axios.post<{ file: string }>(
    `/organizations/${organizationId}/meetings/${meetingId}/file`,
    formData,
  );
  return response.data;
};

export const getOrganizationMeetings = async (organizationId: string) => {
  const response = await axios.get<{ meetings: Meeting[] }>(
    `/organizations/${organizationId}/meetings`,
  );
  return response.data;
};

export const updateOrganizationMeeting = async (
  data: { organizationId: string; meetingId: string },
  updatedData: any,
) => {
  const response = await axios.put(
    `/organizations/${data.organizationId}/meetings/${data.meetingId}`,
    updatedData,
  );
  return response.data;
};

export const deleteOrganizationMeeting = async ({
  organizationId,
  meetingId,
}: {
  organizationId: string;
  meetingId: string;
}) => {
  const response = await axios.delete(`/organizations/${organizationId}/meetings/${meetingId}`);
  return response.data;
};

export const createOrganizationCheckoutSession = async (
  id: string,
  { product_id }: { product_id: string },
) => {
  const response = await client2.post<{ result: { url: string }; success: boolean }>(
    `/organizations/${id}/create-checkout-session`,
    { product_id },
  );
  return response.data;
};

export const cancelOrganizationSubscription = async (organizationId: string) => {
  const response = await axios.post(`organizations/${organizationId}/subscription/cancel`);
  return response.data;
};

export const getOrganizationFeaturesCount = async (id: string) => {
  const response = await axios.get<{ jobs: number; sortlist: number }>(
    `/organizations/${id}/features-count`,
  );
  return response.data;
};

export const getTeammates = async (organizationId: string) => {
  const response = await axios.get<{ teammates: Teammate[] }>(
    `/organizations/${organizationId}/teammates`,
  );
  return response.data;
};

export const createTeammate = async (
  organizationId: string,
  teammate: {
    full_name: string;
    email: string;
    password: string;
    role: string;
    permissions: string[];
    permitted_locations: { state: string; city: string; zip_code: string }[];
    permitted_all_locations: boolean;
  },
) => {
  const response = await axios.post<Teammate>(
    `/organizations/${organizationId}/teammates`,
    teammate,
  );
  return response.data;
};

export const updateTeammate = async (
  { organizationId, teammateId }: { organizationId: string; teammateId: string },
  teammate: {
    full_name?: string;
    email?: string;
    role?: string;
    permissions?: string[];
    permitted_locations?: { state: string; city: string; zip_code: string }[];
    permitted_all_locations: boolean;
  },
) => {
  const response = await axios.put<Teammate>(
    `/organizations/${organizationId}/teammates/${teammateId}`,
    teammate,
  );
  return response.data;
};

export const deleteTeammate = async ({
  organizationId,
  teammateId,
}: {
  organizationId: string;
  teammateId: string;
}) => {
  const response = await axios.delete(`/organizations/${organizationId}/teammates/${teammateId}`);
  return response.data;
};

export const getOrganizationJobs = async (organizationId: string) => {
  const response = await axios.get<{ jobs: Job[] }>(`/organizations/${organizationId}/jobs`);
  return response.data;
};
